import type { ClientItem, UserCapacity } from './api/types';
import type { StaffName } from './types';

export function formatHours(value: number): string {
  return `${value.toFixed(2)}h`;
}

// Every result the search found, each labelled with the owner it
// would be taken from — a client has exactly one owner, so assigning is always
// a move and the admin should see whose work they are moving.
export function assignableClientOptions(
  clients: ClientItem[],
  userId: string,
  ownerNameById: ReadonlyMap<string, string>,
): Array<{ value: string; label: string; disabled: boolean }> {
  return clients.map((client) => {
    const current = client.ownerUserId ? ownerNameById.get(client.ownerUserId) : null;
    return {
      value: client.id,
      label: current ? `${client.name} — ${current}` : client.name,
      disabled: client.ownerUserId === userId,
    };
  });
}

export function ownerNames(staff: StaffName[]): Map<string, string> {
  return new Map(staff.map((member) => [member.id, member.name]));
}

// Capacity reads each member's hours through their own ClickUp token, so a
// missing token or a failed sync means their time is missing from every total.
export function capacitySyncLabel(
  sync: UserCapacity['capacitySync'],
  clickupConnected: boolean,
): string {
  if (!clickupConnected) return 'No ClickUp token: Capacity cannot read this member’s hours.';
  if (!sync) return 'Capacity has not synced this member’s ClickUp hours for this month yet.';
  const when = new Date(sync.syncedAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return sync.status === 'failed'
    ? `Last sync failed (${when}): ${sync.message ?? 'no reason given'}. Their hours may be stale.`
    : `ClickUp hours last synced ${when}.`;
}
